import { authSelectors } from 'ducks/auth'
import * as Sentry from '@sentry/react'
import { orgSelectors } from 'ducks/orgs'
import {
  isSupplierPreferenceFeatureEnabled,
} from 'projectSections/designPanels/designRules/preferredSupplierUtils'
import { RootState } from 'types/state'
import { fetchComponentsV2, V2ComponentBase } from 'util/fetchComponentsV2'
import { FeatureChecker } from 'util/featureFlags'
import { Item, MountingCalcResult } from '../../types'
import { pickSupplierDefaultByPreference } from '../../supplierPreferenceDefaults'

export type V2ComponentRow = V2ComponentBase

/** Obsolete SKUs replaced unconditionally (not supplier-dependent). */
export const GSE_DIRECT_REPLACEMENTS: Readonly<Record<string, string>> = {
  ART101088: 'ART106373',
}

/**
 * Interchangeable SKUs for the same GSE accessory. The first entry is the
 * canonical code used during BOM generation; later entries are newer
 * equivalents stocked by different suppliers.
 */
export const GSE_COMPONENT_ALTERNATIVES: Readonly<Record<string, readonly string[]>> = {
  VIS2014N: ['VIS2014N', 'ART106051', 'ART101552'],
  ART105155: ['ART105155', 'ART105162'],
  ART100039: ['ART100039', 'ART106394'],
  ART100385: ['ART100385', 'ART106352', 'FFR5-370B'],
  ART100495: ['ART100495', 'ART105911'],
  ART100574: ['ART100574', 'ART106408'],
  ART100633: ['ART100633', 'ART103209'],
  ART100744: ['ART100744', 'ART105918'],
  ART100945: ['ART100945', 'ART106401'],
  ART101041: ['ART101041', 'ART106387'],
  ART101405: ['ART101405', 'FFR5-560B'],
  ART102328: ['ART102328', 'ART106751'],
  ART102335: ['ART102335', 'ART106716'],
  'GSE-WEDGES-TWINPACK': ['GSE-WEDGES-TWINPACK', 'ART105876'],
  'GSE-FRAME-DPO-1650-1135 GSE GSE Frame Portrait -1650/1135': [
    'GSE-FRAME-DPO-1650-1135 GSE GSE Frame Portrait -1650/1135',
    'ART105162',
  ],
}

export type GseSubstitutionContext = {
  orgId: number
  orderedSuppliers: string[]
  countryIso2?: string
}

export function getGseCandidateCodes(code: string): readonly string[] {
  return GSE_COMPONENT_ALTERNATIVES[code] ?? [GSE_DIRECT_REPLACEMENTS[code] ?? code]
}

export function resolveGseComponentCode(
  code: string,
  components: V2ComponentRow[],
  orderedSuppliers: string[]
): string {
  if (GSE_DIRECT_REPLACEMENTS[code]) {
    return GSE_DIRECT_REPLACEMENTS[code]
  }

  const alternatives = GSE_COMPONENT_ALTERNATIVES[code]
  if (!alternatives?.length || !orderedSuppliers.length || !components.length) {
    return code
  }

  return pickSupplierDefaultByPreference(components, orderedSuppliers, alternatives) ?? code
}

export function getGseSubstitutionContext(isFeatureEnabled: FeatureChecker): GseSubstitutionContext | null {
  if (!isSupplierPreferenceFeatureEnabled(isFeatureEnabled)) return null

  const state = window.reduxStore?.getState() as RootState | undefined
  if (!state) return null

  const supplierPreferences = orgSelectors.getOrgSupplierPreferences(state) ?? []
  const enabledDistributors = orgSelectors.getEnabledHardwareDistributorFilterKeys(state)
  const orderedSuppliers = supplierPreferences.filter((k) => enabledDistributors.includes(k))
  if (!orderedSuppliers.length) return null

  const orgId = authSelectors.getOrgId(state)
  if (!orgId) return null

  return {
    orgId,
    orderedSuppliers,
    countryIso2: orgSelectors.getOrgIso2(state),
  }
}

function collectUniqueCodes(result: MountingCalcResult): Set<string> {
  const codes = new Set<string>()
  result.items.forEach((item) => {
    codes.add(item.name)
    item.components.forEach((component) => codes.add(component.name))
  })
  return codes
}

function buildSubstitutionCodeMap(
  codes: Set<string>,
  components: V2ComponentRow[],
  orderedSuppliers: string[]
): Map<string, string> {
  const codeMap = new Map<string, string>()
  codes.forEach((code) => {
    codeMap.set(code, resolveGseComponentCode(code, components, orderedSuppliers))
  })
  return codeMap
}

function applyCodeMapToItems(items: Item[], codeMap: Map<string, string>): Item[] {
  return items.map((item) => ({
    ...item,
    name: codeMap.get(item.name) ?? item.name,
    components: item.components.map((component) => ({
      ...component,
      name: codeMap.get(component.name) ?? component.name,
    })),
  }))
}

function buildComponentFetchFilters(candidateCodes: string[], countryIso2?: string): Record<string, string> {
  const filters: Record<string, string> = {
    codes: candidateCodes.join(','),
    limit: String(candidateCodes.length),
  }
  if (countryIso2) filters.country_iso2 = countryIso2
  return filters
}

/**
 * Rewrites GSE BOM item codes: applies obsolete replacements unconditionally,
 * then picks interchangeable alternatives based on supplier preference + stock.
 */
export async function substituteGseBomComponents(
  result: MountingCalcResult,
  isFeatureEnabled?: FeatureChecker
): Promise<MountingCalcResult> {
  const codes = collectUniqueCodes(result)

  const needsSubstitution = [...codes].some(
    (code) => GSE_DIRECT_REPLACEMENTS[code] || GSE_COMPONENT_ALTERNATIVES[code]
  )
  if (!needsSubstitution) return result

  const substitutionCtx = isFeatureEnabled ? getGseSubstitutionContext(isFeatureEnabled) : null
  const orderedSuppliers = substitutionCtx?.orderedSuppliers ?? []

  let components: V2ComponentRow[] = []
  if (substitutionCtx) {
    const candidateCodes = Array.from(new Set([...codes].flatMap((code) => [...getGseCandidateCodes(code)])))
    try {
      components = await fetchComponentsV2<V2ComponentRow>(
        substitutionCtx.orgId,
        buildComponentFetchFilters(candidateCodes, substitutionCtx.countryIso2)
      )
    } catch (err) {
      // Fall back to direct replacements only when the catalogue fetch fails.
      Sentry.captureMessage('GSE supplier substitution catalogue fetch failed', {
        level: 'warning',
        tags: { component: 'mounting', integration: 'gse' },
        extra: { errorName: err instanceof Error ? err.name : undefined },
      })
      components = []
    }
  }

  const codeMap = buildSubstitutionCodeMap(codes, components, orderedSuppliers)

  return {
    ...result,
    items: applyCodeMapToItems(result.items, codeMap),
  }
}
